/**
 * The Character model (CONTEXT.md, "Character"): a progression log.
 *
 * A Character is a list of Levels 1 to 20. Each Level owns the Choices the rules opened at that
 * Level, and each Choice holds at most one Selection, referenced by Foundry UUID. Nothing here
 * copies Entry data; the rules engine (issue #5 onward) resolves Selections against the Snapshot.
 *
 * Shapes the types forbid on purpose: a Selection outside a Choice, a Choice outside a Level, a
 * Level number outside 1 to 20.
 */

import type { RollLog } from "../session/log";
import type { Session } from "../session/model";

export type LevelNumber =
  | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
  | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;

export const LEVEL_NUMBERS: readonly LevelNumber[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

export function isLevelNumber(n: number): n is LevelNumber {
  return Number.isInteger(n) && n >= 1 && n <= 20;
}

/**
 * What opened a Choice. Together with the Level this identifies the Choice stably across
 * rebuilds, so Retraining and Snapshot migration (#17) can address it again.
 */
export type ChoiceSource =
  /** The four foundations, opened once at Level 1. */
  | { kind: "ancestry" }
  | { kind: "heritage" }
  | { kind: "background" }
  | { kind: "class" }
  /** The second class under the Dual Class Variant Rule (#12), opened at Level 1 while the rule is on. */
  | { kind: "dual-class" }
  /**
   * A slot from the progression tables: a class feat at 2, a skill increase at 3, an ancestry
   * feat at 5, a general feat at 3 ... `slot` is the table's name for it; `ordinal` separates
   * several of the same slot at one Level (two skill increases, say).
   */
  | { kind: "progression"; slot: ProgressionSlot; ordinal: number }
  /**
   * A Choice a granting Entry's GrantItem Rule Element opened: `uuid` is the granting Entry and
   * `index` the position of the Rule Element in its `rules`. Its Selection is the granted Entry.
   * Filled by the engine (src/character/grants.ts), never by the user.
   */
  | { kind: "rule-element"; uuid: string; index: number }
  /**
   * A Choice a granting Entry's feature list opened: the class features on a class Entry, the
   * ancestry features on an ancestry Entry (Foundry's `system.items`). `uuid` is the granting
   * Entry and `id` the row's key in that list. Filled by the engine like a rule-element Choice.
   */
  | { kind: "feature"; uuid: string; id: string };

export type ProgressionSlot =
  | "ancestry-feat"
  | "class-feat"
  | "class-feature"
  | "general-feat"
  | "skill-feat"
  | "skill-increase"
  | "attribute-boosts"
  | "archetype-feat"
  | "ancestry-paragon-feat"
  | "dual-class-feat"
  /** Automatic Bonus Progression: a skill potency step at 3, 6, 9, 13, 15 and 20, answered with a skill (`skill`). */
  | "skill-potency"
  /** Automatic Bonus Progression: the attribute apex at 17, answered with an attribute (`attribute`). */
  | "attribute-apex"
  /** A Familiar ability (#13): one Choice per ability slot the master's Entries open at the Level, filled from the familiar-abilities Pack. */
  | "familiar-ability"
  /** An animist's attuned apparition (#21): two Choices at Apparition Attunement, one more at Third and Fourth Apparition, filled from the apparition class features. */
  | "apparition";

/** A Selection: the Entry a Choice was filled with, by Foundry UUID, and the Snapshot it was chosen from. */
export interface Selection {
  uuid: string;
  snapshotTag: string;
}

/** A value an Entry's prompt was answered with. Objects appear where a ChoiceSet's choices are objects (an element with its damage type). */
export type AnswerValue = string | number | boolean | { [key: string]: AnswerValue };

export interface Choice {
  source: ChoiceSource;
  /** Absent while the Choice is open. */
  selection?: Selection;
  /**
   * Answers to the prompts the Selection asked. Kept on the Choice so they travel with the
   * Selection on Retraining. Keys the engine reads (src/engine):
   * - a ChoiceSet's `flag` -> the chosen value: a skill, a weapon group, another Entry's UUID ...
   *   (Foundry keeps the same values under `flags.pf2e.rulesSelections`);
   * - ancestry: `boost:<n>` / `flaw:<n>` for slot n of the Entry's boosts and flaws,
   *   `voluntary:boost`, `voluntary:flaw:0`, `voluntary:flaw:1`, `alternate:0`, `alternate:1`;
   * - background: `boost:<n>`;
   * - class: `keyAbility`, and `skill:<n>` for each free trained skill (a skill slug or a Lore name);
   * - the `attribute-boosts` slot: `boost`, one Choice per boost;
   * - the `skill-increase` slot: `skill`;
   * - the `skill-potency` slot: `skill`; the `attribute-apex` slot: `attribute` (Automatic Bonus Progression, #12).
   * A Choice from the last four slots has answers and no Selection.
   */
  answers?: Record<string, AnswerValue>;
  /**
   * On a granted Choice (rule-element or feature source): the granting Selection was removed
   * with `onDeleteActions.granter` set to `detach`, so this Selection stays as if taken directly.
   * The engine leaves detached Choices alone when it reconciles grants.
   */
  detached?: true;
  /**
   * A Snapshot migration (#17) reopened this Choice, or one of its Answers: the warning the
   * Builder shows beside it until it is filled again. Filling the Choice (or answering the flag)
   * clears the record.
   */
  reopened?: Reopened[];
}

/** Why a Snapshot migration reopened a Choice or an Answer (CONTEXT.md, "Snapshot migration"). */
export interface Reopened {
  /** The Snapshot the Character was saved under before the migration, and the one it moved to. */
  from: string;
  to: string;
  /** The Answer's flag when an Answer was reopened; null when the Selection itself was. */
  flag: string | null;
  /** The Entry the Selection or Answer held before, by its old UUID, and its name when the previous Snapshot's index was at hand. */
  uuid: string | null;
  name: string | null;
  /** `missing`: the Entry is not in the new Snapshot and no redirect names its successor; `illegal`: it is, but the Selection is no longer structurally legal. */
  reason: "missing" | "illegal";
  /** The guide's reason for an illegal one; null for a missing one. */
  detail: string | null;
}

export interface Level {
  number: LevelNumber;
  choices: Choice[];
}

/** The state of one toggleable RollOption: whether it is on, and which suboption is chosen. */
export interface ToggleState {
  checked?: boolean;
  selection?: string;
}

/** A manual value replacing a computed statistic. Always shown as such on the sheet. */
export interface Override {
  /**
   * The statistic it replaces, by the engine's key: `ac`, `hp-max`, `perception`, `initiative`,
   * `fortitude` / `reflex` / `will`, a skill slug, `class-dc`, `land-speed`, `ability:<str|dex|...>`
   * (see `overrideTargets` in src/engine).
   */
  statistic: string;
  value: number;
  /** Optional reason the user gave, shown beside the Override. */
  note?: string;
}

/**
 * An immunity, weakness or resistance entered by hand on the Character (#22), beside the ones
 * Rule Elements derive. Seeded into the engine before the Rule Elements run, so Foundry's merge
 * applies: the highest value wins for a duplicate type, an `override` rule replaces it, a
 * `remove` rule removes it.
 */
export interface ManualIWR {
  kind: "immunity" | "weakness" | "resistance";
  /** A type from the Snapshot's immunity, weakness or resistance table (`fire`, `physical`, `all-damage`). */
  type: string;
  /** Weaknesses and resistances: a whole number of 1 or more. Absent on an immunity. */
  value?: number;
  /** Types the entry does not apply to ("physical 5 (except silver)"). */
  exceptions?: string[];
  /** Resistances only: types the value doubles against. */
  doubleVs?: string[];
  /** Optional reason the user gave, shown beside the entry like an Override's. */
  note?: string;
}

/** GM Core Variant Rules toggled per Character (docs/design.md, "Rules engine"). All off by default. */
export interface VariantRules {
  freeArchetype: boolean;
  automaticBonusProgression: boolean;
  gradualAttributeBoosts: boolean;
  ancestryParagon: boolean;
  dualClass: boolean;
  proficiencyWithoutLevel: boolean;
}

export const DEFAULT_VARIANT_RULES: Readonly<VariantRules> = Object.freeze({
  freeArchetype: false,
  automaticBonusProgression: false,
  gradualAttributeBoosts: false,
  ancestryParagon: false,
  dualClass: false,
  proficiencyWithoutLevel: false,
});

export type MagicTradition = "arcane" | "divine" | "occult" | "primal";
export const MAGIC_TRADITIONS: readonly MagicTradition[] = ["arcane", "divine", "occult", "primal"];
/** Foundry's casting categories; `items` is a staff's, whose spells cost charges (#21). */
export type CastingType = "prepared" | "spontaneous" | "innate" | "focus" | "items";
export const CASTING_TYPES: readonly CastingType[] = ["prepared", "spontaneous", "innate", "focus", "items"];
export type SpellcastingAttribute = "str" | "dex" | "con" | "int" | "wis" | "cha";

/**
 * A spell the user gave a Spellcasting Entry (docs/adr/0003): a repertoire spell, a spellbook
 * spell, a chosen innate or focus spell. Spells a Casting Source grants on its own are derived by
 * the engine and never stored.
 */
export interface KnownSpell {
  uuid: string;
  /** Spontaneous and innate Entries: the rank the spell is known at (heightened); absent means its base rank. */
  rank?: number;
  /** Spontaneous Entries: a signature spell, castable at any rank the Entry has slots for. */
  signature?: true;
  /** The spell Choice this answers (`SpellChoiceView.key`), when a Casting Source asked for it; absent for a free pick. */
  choice?: string;
  /** The Level the spell was learned at, when the user learned it at a Level. */
  level?: LevelNumber;
}

/** The user's spells on one Spellcasting Entry, keyed by the Entry's stable id (`SpellcastingEntryView.id`). */
export interface SpellcastingState {
  known: KnownSpell[];
  /** Prepared Entries: by rank ("0" cantrips to "10"), the spell UUID prepared in each slot, null when empty. */
  prepared: Record<string, (string | null)[]>;
}

/** A Spellcasting Entry the user declared by hand, for a Casting Source the app's table does not know (docs/adr/0003). */
export interface ManualSpellcastingEntry {
  /** `manual:<n>`, unique within the Character. */
  id: string;
  name: string;
  tradition: MagicTradition;
  casting: CastingType;
  attribute: SpellcastingAttribute;
  /** Slots per rank ("1" to "10"; "0" for prepared cantrips). */
  slots: Record<string, number>;
}

export interface CharacterSpellcasting {
  entries: Record<string, SpellcastingState>;
  manual: ManualSpellcastingEntry[];
}

export function emptySpellcasting(): CharacterSpellcasting {
  return { entries: {}, manual: [] };
}

/* ---------------------------------- Familiar ---------------------------------- */

/**
 * The Character's Familiar (CONTEXT.md, "Familiar"): what the user sets on it. Its abilities are
 * `familiar-ability` progression Choices in the log; its statistics derive from the master's
 * sheet (src/engine/familiar.ts). Exists only for what the derived Familiar cannot know.
 */
export interface Familiar {
  name: string;
  /**
   * The master's attribute the Familiar's Perception, Acrobatics and Stealth use when the rules'
   * default (the spellcasting attribute, else Charisma) is not the one wanted; null for the default.
   */
  attribute: SpellcastingAttribute | null;
  /** Overrides on the Familiar's statistics, keyed like the Character's (`hp-max`, `ac`, a save, `perception`, `attack`, a skill slug, `land-speed`). */
  overrides: Override[];
}

export function emptyFamiliar(name = "Familiar"): Familiar {
  return { name, attribute: null, overrides: [] };
}

/* ---------------------------------- Inventory ---------------------------------- */

/** How a physical Entry is carried (Foundry's carry types). */
export type CarryType = "held" | "worn" | "stowed" | "dropped";
export const CARRY_TYPES: readonly CarryType[] = ["held", "worn", "stowed", "dropped"];

/** Runes etched on one copy of an Entry, over the Entry's own. */
export interface ItemRunes {
  potency?: number;
  striking?: number;
  resilient?: number;
  reinforcing?: number;
  /** Property rune slugs as Foundry names them ("flaming", "greaterFlaming"). */
  property?: string[];
}

export interface ItemMaterial {
  type: string;
  grade: "low" | "standard" | "high";
}

export interface TraitToggles {
  versatile?: string | null;
  modular?: number | null;
  doubleBarrel?: boolean;
}

/**
 * One physical Entry the Character owns (CONTEXT.md, "Inventory"): a copy of an equipment Entry
 * by UUID with its quantity, how it is carried, where it sits, and what was etched on it.
 */
export interface InventoryItem {
  /** `i<n>`, unique within the Character. */
  id: string;
  uuid: string;
  quantity: number;
  carryType: CarryType;
  /** Hands holding it when held. */
  handsHeld: 0 | 1 | 2;
  /** Worn items with a slot (armour, shoes, a cloak): whether it is worn in that slot rather than merely on the person. */
  inSlot?: boolean;
  /** Items with the invested trait: whether invested (counts against the ten). */
  invested?: boolean;
  /** The container item's id when inside one. */
  container?: string;
  runes?: ItemRunes;
  material?: ItemMaterial;
  /** The copy's size when it differs from the Entry's (gear sized for a Large Character). */
  size?: string;
  /** Current Hit Points of a shield or other item with HP, when damaged. */
  hp?: number;
  /** Weapons: the inventory id of the ammunition stack selected. */
  ammo?: string;
  traitToggles?: TraitToggles;
}

export interface Coins {
  pp?: number;
  gp?: number;
  sp?: number;
  cp?: number;
}

export interface Inventory {
  items: InventoryItem[];
  coins: Coins;
  /** The next item id number. */
  counter: number;
}

export function emptyInventory(): Inventory {
  return { items: [], coins: {}, counter: 1 };
}

/* ---------------------------------- Snapshot migration ---------------------------------- */

/** A Choice or Answer a Snapshot migration reopened, as the migration report lists it. */
export interface MigrationReopened extends Reopened {
  key: string;
  level: LevelNumber;
  label: string;
}

/** Something a Snapshot migration removed because its Entry is not in the new Snapshot: a granted Selection, a spell, an inventory item, a Session item. */
export interface MigrationRemoved {
  kind: "grant" | "spell" | "item" | "condition" | "effect" | "consumable";
  uuid: string;
  name: string | null;
  /** Where it was: the Choice key of a grant, the Spellcasting Entry id of a spell, the quantity of an item. */
  detail: string | null;
}

/**
 * The record of one Snapshot migration (#17): the Character was saved under `from` and now
 * stands under `to`. Kept on the Character; shown once as the migration report, then `seen`.
 */
export interface SnapshotMigration {
  from: string;
  to: string;
  /** ISO timestamp. */
  at: string;
  /** How many UUIDs the redirects replaced: Selections, granting Entries, Answers, spells, items, Session items. */
  redirected: number;
  reopened: MigrationReopened[];
  removed: MigrationRemoved[];
  /** Whether the report has been shown and dismissed. */
  seen: boolean;
}

/** Bump when the Character shape changes; `migrateCharacter` in ./migrate.ts must handle every older value. */
export const CHARACTER_SCHEMA = 9 as const;

export interface Character {
  schema: typeof CHARACTER_SCHEMA;
  /** UUID v4, generated on the device. */
  id: string;
  name: string;
  /** ISO timestamps. */
  createdAt: string;
  updatedAt: string;
  /** The Snapshot release the Character was last saved under (e.g. "pf2e-8.5.0"), for migration on a bump (#17). */
  snapshotTag: string;
  /** The Level the Character is currently played at; Levels above it exist only when the user has planned ahead. */
  level: LevelNumber;
  /** Ascending by `number`, no gaps, starting at 1. Only Levels that have been opened are present. */
  levels: Level[];
  variantRules: VariantRules;
  overrides: Override[];
  /** Immunities, weaknesses and resistances entered by hand (#22), merged with the derived ones. */
  iwr: ManualIWR[];
  /**
   * Toggleable RollOptions the user switched (a stance, Double Slice's second attack), keyed
   * `${domain}:${option}` as the engine's toggles are. Saved with the Character; a Session (#18)
   * may overlay its own for the encounter.
   */
  toggles: Record<string, ToggleState>;
  /** Free-form player notes. */
  notes: string;
  /** The spells the user gave each Spellcasting Entry, and any Entries declared by hand (#10). */
  spellcasting: CharacterSpellcasting;
  /** The physical Entries the Character owns and its coins (#11). */
  inventory: Inventory;
  /** The open Session (#18), or null when the Character is not in play. */
  session: Session | null;
  /** The Roll Log (#20): the Session's Play Actions in order. Empty whenever `session` is null; cleared by start fresh. */
  rollLog: RollLog;
  /** The Familiar's name, attribute and Overrides (#13); null until the user touches them (the derived Familiar uses defaults). */
  familiar: Familiar | null;
  /** Every Snapshot migration the Character went through (#17), oldest first. */
  migrations: SnapshotMigration[];
}

/** A stable string key for a Choice. Equal keys mean the same Choice across rebuilds. */
export function choiceKey(level: LevelNumber, source: ChoiceSource): string {
  switch (source.kind) {
    case "ancestry":
    case "heritage":
    case "background":
    case "class":
    case "dual-class":
      return `${level}:${source.kind}`;
    case "progression":
      return `${level}:progression:${source.slot}:${source.ordinal}`;
    case "rule-element":
      return `${level}:rule-element:${source.uuid}:${source.index}`;
    case "feature":
      return `${level}:feature:${source.uuid}:${source.id}`;
  }
}

/** Reverse of `choiceKey`. Returns undefined for a string `choiceKey` could not have produced. */
export function parseChoiceKey(key: string): { level: LevelNumber; source: ChoiceSource } | undefined {
  const m = /^(\d+):(.*)$/s.exec(key);
  if (!m) return undefined;
  const level = Number(m[1]);
  if (!isLevelNumber(level)) return undefined;
  const rest = m[2]!;
  if (rest === "ancestry" || rest === "heritage" || rest === "background" || rest === "class" || rest === "dual-class") {
    return { level, source: { kind: rest } };
  }
  const prog = /^progression:([a-z-]+):(\d+)$/.exec(rest);
  if (prog) return { level, source: { kind: "progression", slot: prog[1] as ProgressionSlot, ordinal: Number(prog[2]) } };
  // UUIDs contain dots and colons but never a trailing ":<digits>" of their own, so the last segment is the index.
  const rule = /^rule-element:(.+):(\d+)$/s.exec(rest);
  if (rule) return { level, source: { kind: "rule-element", uuid: rule[1]!, index: Number(rule[2]) } };
  const feature = /^feature:(.+):([^:]+)$/s.exec(rest);
  if (feature) return { level, source: { kind: "feature", uuid: feature[1]!, id: feature[2]! } };
  return undefined;
}

/** Whether a Choice was opened by another Selection's Entry (the engine fills and removes it). */
export function isGranted(source: ChoiceSource): source is Extract<ChoiceSource, { kind: "rule-element" | "feature" }> {
  return source.kind === "rule-element" || source.kind === "feature";
}

export function isOpen(choice: Choice): boolean {
  return choice.selection === undefined;
}

/** The progression slots whose Choice holds an answer instead of a Selection. */
const ANSWER_SLOTS: Record<Extract<ProgressionSlot, "attribute-boosts" | "skill-increase" | "skill-potency" | "attribute-apex">, string> = { "attribute-boosts": "boost", "skill-increase": "skill", "skill-potency": "skill", "attribute-apex": "attribute" };

/**
 * Whether a Choice still wants the user: no Selection, or for a boost or skill increase no
 * answer. Granted Choices are the engine's and never unfilled. Answers a Selection's ChoiceSets
 * still want are the guide's to count (they need the Entry).
 */
export function isUnfilled(choice: Choice): boolean {
  if (isGranted(choice.source)) return false;
  if (choice.source.kind === "progression" && choice.source.slot in ANSWER_SLOTS) {
    const flag = ANSWER_SLOTS[choice.source.slot as keyof typeof ANSWER_SLOTS];
    const value = choice.answers?.[flag];
    return typeof value !== "string" || value === "";
  }
  return choice.selection === undefined;
}

/** How many Choices up to the Character's Level are unfilled (the list's badge; the guide counts open Answers too). */
export function countUnfilled(character: Character): number {
  let n = 0;
  for (const level of character.levels) {
    if (level.number > character.level) break;
    for (const choice of level.choices) if (isUnfilled(choice)) n++;
  }
  return n;
}

/** Find a Choice by key. */
export function findChoice(character: Character, key: string): Choice | undefined {
  for (const level of character.levels) {
    for (const choice of level.choices) {
      if (choiceKey(level.number, choice.source) === key) return choice;
    }
  }
  return undefined;
}

/** The kinds of Choice filled with a foundation Entry: the four every Character opens at Level 1, and the Dual Class second class. */
export type FoundationKind = "ancestry" | "heritage" | "background" | "class" | "dual-class";

/** The Selection filling one of the Level 1 foundations (the Dual Class second class included), if made. */
export function foundation(character: Character, kind: FoundationKind): Selection | undefined {
  return findChoice(character, choiceKey(1, { kind }))?.selection;
}
